import { useState, useCallback } from "react";
import { useDealContext } from "../hooks/useDealContext";
import { useParams } from "react-router-dom";
import { fetchScenarios, type ScenarioResponse } from "../api/client";
import { useEffect } from "react";
import { fmtCurrency, fmtPercent } from "../utils/format";
import { DollarSign, TrendingUp, AlertTriangle } from "lucide-react";
import EmptyState from "../components/EmptyState";

/**
 * Expense categories with their controllability classification.
 */
const EXPENSE_CATEGORIES = [
  { key: "real_estate_taxes", label: "Real Estate Taxes", controllable: false },
  { key: "insurance", label: "Insurance", controllable: false },
  { key: "utilities", label: "Utilities", controllable: false },
  { key: "repairs_and_maintenance", label: "Repairs & Maintenance", controllable: true },
  { key: "janitorial", label: "Janitorial", controllable: true },
  { key: "landscaping", label: "Landscaping", controllable: true },
  { key: "security", label: "Security Services", controllable: true },
  { key: "management_fee", label: "Management Fee", controllable: true },
  { key: "administrative", label: "Administrative", controllable: true },
] as const;

export default function ExpensesPage() {
  const { deal } = useDealContext();
  const { dealId } = useParams<{ dealId: string }>();
  const [scenarios, setScenarios] = useState<ScenarioResponse[]>([]);

  useEffect(() => {
    if (dealId) fetchScenarios(dealId).then(setScenarios).catch(() => {});
  }, [dealId]);

  const activeScenario = scenarios.find((s) => s.projection_result) ?? scenarios[0];
  const pr = activeScenario?.projection_result as any;
  const expAudit = pr?.yearly_expense_audit as any[] | undefined;
  const assumptions = activeScenario?.assumptions as Record<string, any> | undefined;
  const overrides = activeScenario?.assumption_overrides as Record<string, Record<string, number>> | undefined;

  // Get base year expenses from property data
  const opex = (deal?.property_data?.operating_expenses as any[]) || [];
  const baseYear = opex.length > 0 ? opex[opex.length - 1] : null;

  if (!baseYear) {
    return (
      <EmptyState
        icon={DollarSign}
        title="No expense data"
        description="Upload an operating statement to populate expense data."
      />
    );
  }

  const expGrowth = assumptions?.expense_growth_rate_pct ?? deal?.property_data?.expense_growth_rate_pct ?? 0.03;
  const taxGrowth = assumptions?.tax_growth_rate_pct ?? deal?.property_data?.tax_growth_rate_pct ?? expGrowth;
  const insGrowth = assumptions?.insurance_growth_rate_pct ?? deal?.property_data?.insurance_growth_rate_pct ?? expGrowth;
  const holdYears = deal?.property_data?.hold_period_years ?? 10;

  const getGrowthRate = (key: string): number => {
    if (key === "real_estate_taxes") return taxGrowth;
    if (key === "insurance") return insGrowth;
    return expGrowth;
  };

  // Timing context
  const baseExpYear = baseYear.year as number | undefined;
  const analysisStartStr = (assumptions?.analysis_start_date ?? deal?.property_data?.analysis_start_date ?? "") as string;
  const analysisStartYear = analysisStartStr ? new Date(analysisStartStr).getFullYear() : new Date().getFullYear();
  const growthYears = baseExpYear ? Math.max(0, analysisStartYear - baseExpYear) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-semibold text-bark-800">Expenses</h2>
        <p className="text-sm text-bark-400 mt-1">
          Projected operating expenses by category, based on base year amounts and growth rates. To adjust assumptions, use the Pro Forma page or AutoVal Chat.
        </p>
      </div>

      {/* Timing context bar */}
      {baseExpYear && (
        <div className="flex items-center gap-4 px-4 py-3 bg-grove-50 border border-grove-200 rounded-lg text-sm">
          <TrendingUp className="w-4 h-4 text-forest-600 flex-shrink-0" />
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-bark-600">
            <span>Base Year: <strong>{baseExpYear}</strong></span>
            <span>Analysis Start: <strong>{analysisStartYear}</strong></span>
            <span>
              Growth Applied: <strong>{growthYears} year{growthYears !== 1 ? "s" : ""}</strong> at {fmtPercent(expGrowth)}
            </span>
          </div>
          {growthYears === 0 && (
            <span className="ml-auto text-xs text-bark-400">Base year matches analysis start — no growth applied to Year 1</span>
          )}
          {growthYears >= 3 && (
            <span className="ml-auto flex items-center gap-1 text-xs text-honey-700">
              <AlertTriangle className="w-3 h-3" />
              {growthYears}+ years of growth applied. Consider uploading more recent operating statements.
            </span>
          )}
        </div>
      )}

      {/* Growth rate summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-grove-300 p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-bark-400">Expense Growth</div>
          <div className="text-lg font-semibold text-bark-700 mt-1">{fmtPercent(expGrowth)}</div>
        </div>
        <div className="bg-white rounded-xl border border-grove-300 p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-bark-400">Tax Growth</div>
          <div className="text-lg font-semibold text-bark-700 mt-1">{fmtPercent(taxGrowth)}</div>
        </div>
        <div className="bg-white rounded-xl border border-grove-300 p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-bark-400">Insurance Growth</div>
          <div className="text-lg font-semibold text-bark-700 mt-1">{fmtPercent(insGrowth)}</div>
        </div>
      </div>

      {/* Expense table */}
      <div className="bg-white rounded-xl border border-grove-300 overflow-x-auto shadow-sm">
        <table className="w-full data-table text-[13px]">
          <thead>
            <tr>
              <th className="text-left min-w-[200px] sticky left-0 bg-grove-100 z-10">Category</th>
              <th className="num min-w-[130px]">Base Year ({baseYear.year})</th>
              <th className="num min-w-[80px]" data-field-id="expense_growth_rates">Growth</th>
              <th className="num min-w-[60px]">Type</th>
              {Array.from({ length: Math.min(holdYears, 10) }, (_, i) => (
                <th key={i} className="num min-w-[130px]">
                  Year {i + 1}
                  {overrides && Object.values(overrides).some((ym: any) => ym[String(i + 1)]) && (
                    <span className="ml-1 w-1.5 h-1.5 rounded-full bg-honey-500 inline-block" />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {EXPENSE_CATEGORIES.map((cat) => {
              const baseAmount = baseYear[cat.key] ?? 0;
              if (baseAmount === 0) return null;
              const growth = getGrowthRate(cat.key);

              return (
                <tr key={cat.key}>
                  <td className="sticky left-0 bg-white z-10 text-bark-600">
                    {cat.label}
                    <span className={`ml-2 text-[9px] px-1.5 py-0.5 rounded-full ${
                      cat.controllable ? "bg-grove-100 text-bark-400" : "bg-bark-100 text-bark-500"
                    }`}>
                      {cat.controllable ? "Controllable" : "Non-Controllable"}
                    </span>
                  </td>
                  <td className="num font-medium" data-field-id={cat.key}>{fmtCurrency(baseAmount)}</td>
                  <td className="num text-bark-400" data-field-id={`expense_growth_rates.${cat.key}`}>{fmtPercent(growth)}</td>
                  <td className="num text-bark-300 text-[10px]">{cat.controllable ? "C" : "NC"}</td>
                  {Array.from({ length: Math.min(holdYears, 10) }, (_, i) => {
                    const projected = baseAmount * Math.pow(1 + growth, i);
                    return (
                      <td key={i} className="num text-bark-500">
                        {fmtCurrency(projected)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {/* Other expense line items */}
            {((baseYear.other as any[]) ?? []).map((item: any, idx: number) => {
              const amt = item?.amount ?? 0;
              if (amt === 0) return null;
              return (
                <tr key={`other-${idx}`}>
                  <td className="sticky left-0 bg-white z-10 text-bark-600">
                    {item?.name ?? "Other"}
                    <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-grove-100 text-bark-400">
                      Controllable
                    </span>
                  </td>
                  <td className="num font-medium">{fmtCurrency(amt)}</td>
                  <td className="num text-bark-400">{fmtPercent(expGrowth)}</td>
                  <td className="num text-bark-300 text-[10px]">C</td>
                  {Array.from({ length: Math.min(holdYears, 10) }, (_, i) => (
                    <td key={i} className="num text-bark-500">
                      {fmtCurrency(amt * Math.pow(1 + expGrowth, i))}
                    </td>
                  ))}
                </tr>
              );
            })}
            {/* Total row */}
            <tr className="border-t-2 border-grove-300 bg-grove-50">
              <td className="sticky left-0 bg-grove-50 z-10 font-semibold text-bark-700">Total Operating Expenses</td>
              <td className="num font-semibold text-bark-700">
                {fmtCurrency(
                  EXPENSE_CATEGORIES.reduce((s, c) => s + (baseYear[c.key] ?? 0), 0)
                  + ((baseYear.other as any[]) ?? []).reduce((s: number, item: any) => s + (item?.amount ?? 0), 0)
                )}
              </td>
              <td className="num" />
              <td className="num" />
              {Array.from({ length: Math.min(holdYears, 10) }, (_, i) => (
                <td key={i} className="num font-semibold text-bark-700">
                  {fmtCurrency(
                    EXPENSE_CATEGORIES.reduce((s, c) => {
                      const base = baseYear[c.key] ?? 0;
                      const g = getGrowthRate(c.key);
                      return s + base * Math.pow(1 + g, i);
                    }, 0)
                    + ((baseYear.other as any[]) ?? []).reduce((s: number, item: any) => s + (item?.amount ?? 0) * Math.pow(1 + expGrowth, i), 0)
                  )}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-xs text-bark-400">
        Growth rates and overrides are editable via the assumptions panel on the Pro Forma page or through AutoVal Chat.
      </p>
    </div>
  );
}
